import type { LocationIds } from '~/models/Location';
import { isLocationIds } from '~/models/Location';

export interface LocationIdsUpdateEmits {
  (event: 'update:value', ids: LocationIds): void;
}
export interface LocationIdsResetEmits {
  (event: 'reset', ids: LocationIds): void;
}
export interface LocationIdsAddDelEmits {
  (event: 'add', item: { type: 'regions' | 'cities' | 'areas'; value: number }): void;
  (event: 'delete', item: { type: 'regions' | 'cities' | 'areas'; value: number }): void;
}

export interface LocationIdsProps {
  value: LocationIds;
  default: LocationIds;
}

export function useLocationIdsUpdate(emit: LocationIdsUpdateEmits, props: LocationIdsProps) {
  return (ids: LocationIds) => {
    emit('update:value', isLocationIds(ids) ? ids : props.default);
  };
}

export function useLocationIdsReset(
  emit: LocationIdsUpdateEmits & LocationIdsResetEmits,
  props: LocationIdsProps,
  update: ReturnType<typeof useLocationIdsUpdate> = useLocationIdsUpdate(emit, props)
) {
  return (ids = props.default) => {
    update(ids);
    emit('reset', ids);
  };
}

export function useLocationIdsAddItem(
  emit: LocationIdsUpdateEmits & LocationIdsAddDelEmits,
  props: LocationIdsProps,
  update: ReturnType<typeof useLocationIdsUpdate> = useLocationIdsUpdate(emit, props)
) {
  return (type: 'regions' | 'cities' | 'areas', ...values: number[]) => {
    const ids = { regions: props.value.regions, cities: props.value.cities, areas: props.value.areas };
    values.forEach((value) => {
      if (!ids[type].includes(value)) ids[type].push(...values);
      update(ids);
      emit('add', { type, value });
    });
  };
}

export function useLocationIdsDeleteItem(
  emit: LocationIdsUpdateEmits & LocationIdsAddDelEmits,
  props: LocationIdsProps,
  update: ReturnType<typeof useLocationIdsUpdate> = useLocationIdsUpdate(emit, props)
) {
  return (type: 'regions' | 'cities' | 'areas', ...values: number[]) => {
    const ids = { regions: props.value.regions, cities: props.value.cities, areas: props.value.areas };
    values.forEach((value) => {
      ids[type] = ids[type].filter((id) => value !== id);
      update(ids);
      emit('delete', { type, value });
    });
  };
}
